import React from 'react';
import { ChevronDown, Eye, Undo2 } from 'lucide-react';
import { initialsOf } from './nameInitials';
import { apiFetch } from './apiClient';
import { PREVIEW_ALL, enterPreview, exitPreview } from './customerPreview';
// The same fixed-fee-only rule the Risk Register and BPA pickers use, imported rather than copied
// a third time (it has drifted before). Archived projects stay hidden here -- a header menu has
// nowhere sensible to put the "show archived" checkbox those pages have.
import { selectableProjects } from './pages/ProjectRegisters';

// The identity cluster in the top-right of both shells, now a menu: "View as Customer" from the
// internal shell (App.js), "View as Self" from inside the preview (ExternalApp.js). Never rendered
// for a real customer contact -- ExternalApp mounts it only when the principal is a preview.
//
// One component for both directions rather than two: it's the same control in the same corner, and
// which way it goes is the one thing that differs. See customerPreview.js for what a switch does.
export default function ViewAsMenu({ displayName, previewing = false }) {
  const [open, setOpen] = React.useState(false);
  const [projects, setProjects] = React.useState(null); // null until fetched
  const [error, setError] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // On first open, not on mount: most sessions never open this menu, and this is the whole
  // portfolio. Nothing to fetch in the other direction -- leaving the preview needs no list.
  React.useEffect(() => {
    if (!open || previewing || projects) return;
    apiFetch('/projects')
      .then(setProjects)
      .catch((e) => setError(e.message));
  }, [open, previewing, projects]);

  const needle = filter.trim().toLowerCase();
  const eligible = selectableProjects(projects, false);
  const shown = eligible.filter((p) => !needle || p.name.toLowerCase().includes(needle));

  return (
    <div className="header-user-wrap" ref={wrapRef}>
      <button
        className="header-user-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="header-user">{displayName}</span>
        <div className="header-avatar">{initialsOf(displayName)}</div>
        <ChevronDown size={14} strokeWidth={2} aria-hidden />
      </button>

      {open && (
        <div className="header-user-menu" role="menu">
          {previewing ? (
            <>
              <div className="header-user-menu-label">Customer view</div>
              <div className="header-user-menu-hint">
                You're seeing this project the way a customer contact does.
              </div>
              <button className="header-user-menu-item" role="menuitem" onClick={exitPreview}>
                <Undo2 size={15} strokeWidth={1.75} aria-hidden />
                View as Self
              </button>
            </>
          ) : (
            <>
              <div className="header-user-menu-label">
                <Eye size={14} strokeWidth={1.75} aria-hidden /> View as Customer
              </div>
              <div className="header-user-menu-hint">
                Read-only, and limited to what a customer contact for that project can see.
                Fixed-fee (POC) projects only.
              </div>
              {error && <div className="header-user-menu-error">{error}</div>}
              <button
                className="header-user-menu-item"
                role="menuitem"
                onClick={() => enterPreview(PREVIEW_ALL)}
              >
                All projects
              </button>
              {eligible.length > 8 && (
                <input
                  className="form-input"
                  style={{ margin: '4px 0', fontSize: 12, padding: '5px 8px' }}
                  placeholder="Filter projects…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label="Filter projects"
                />
              )}
              <div className="header-user-menu-scroll">
                {shown.map((p) => (
                  <button
                    key={p.id}
                    className="header-user-menu-item"
                    role="menuitem"
                    onClick={() => enterPreview(p.id)}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              {!projects && !error && <div className="header-user-menu-hint">Loading projects…</div>}
              {projects && !shown.length && (
                <div className="header-user-menu-hint">
                  {eligible.length ? 'No projects match.' : 'No fixed-fee projects to view.'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
